import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Send as SendIcon } from '@mui/icons-material';
import { PageCard } from '../common/PageCard';
import { PageHint } from '../common/PageHint';
import { PageToolbar } from '../common/PageToolbar';
import { UniversalPagination } from '../common/UniversalPagination';
import { toastService } from '../../services/toast.service';
import {
  getExpertClientNotifications,
  sendExpertClientNotification,
} from '../../services/notification.service';
import { ExpertClientNotification, CreateExpertNotificationDto } from '../../types/notification';

interface ClientNotificationsTabProps {
  clientId: string;
}

const LEVEL_LABELS: Record<'info' | 'warning' | 'danger', string> = {
  info: 'Інфо',
  warning: 'Увага',
  danger: 'Важливо',
};

const LEVEL_COLORS: Record<'info' | 'warning' | 'danger', 'info' | 'warning' | 'error'> = {
  info: 'info',
  warning: 'warning',
  danger: 'error',
};

export const ClientNotificationsTab: React.FC<ClientNotificationsTabProps> = ({ clientId }) => {
  const [notifications, setNotifications] = useState<ExpertClientNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'read' | 'unread'>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [level, setLevel] = useState<'info' | 'warning' | 'danger'>('info');
  const [sending, setSending] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getExpertClientNotifications(Number(clientId), { page, limit, status: statusFilter });
      setNotifications(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити сповіщення.');
    } finally {
      setLoading(false);
    }
  }, [clientId, page, statusFilter]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const dto: CreateExpertNotificationDto = { message: message.trim(), level };
      await sendExpertClientNotification(Number(clientId), dto);
      toastService.success('Сповіщення надіслано.');
      setMessage('');
      setLevel('info');
      setModalOpen(false);
      setPage(1);
      await fetchNotifications();
    } catch {
      toastService.error('Не вдалося надіслати сповіщення.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box data-i-doc={PageIndices.EXPERT_CLIENT_NOTIFICATIONS}>
      <PageHint iDoc={PageIndices.EXPERT_CLIENT_NOTIFICATIONS} />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Нове сповіщення</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Текст сповіщення..."
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
          <Box sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.75rem', mb: 0.5 }}>Рівень</InputLabel>
            <Select
              value={level}
              onChange={(e: SelectChangeEvent) => setLevel(e.target.value as 'info' | 'warning' | 'danger')}
              fullWidth
            >
              <MenuItem value="info">Інфо</MenuItem>
              <MenuItem value="warning">Увага</MenuItem>
              <MenuItem value="danger">Важливо</MenuItem>
            </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Скасувати</Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={sending || !message.trim()}
          >
            Надіслати
          </Button>
        </DialogActions>
      </Dialog>

      {/* List */}
      <PageToolbar
        storageKey="filters:client-notifications"
        filters={[
          {
            id: 'status',
            type: 'select',
            label: 'Статус',
            value: statusFilter,
            onChange: (v) => { setStatusFilter(v as 'all' | 'read' | 'unread'); setPage(1); },
            minWidth: '160px',
            options: [
              { value: 'all',    label: 'Всі' },
              { value: 'unread', label: 'Непрочитані' },
              { value: 'read',   label: 'Прочитані' },
            ],
          },
        ]}
        onReset={() => { setStatusFilter('all'); setPage(1); }}
        extraActions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
            Надіслати сповіщення
          </Button>
        }
      />

      <PageCard>
        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Повідомлення</TableCell>
                  <TableCell>Рівень</TableCell>
                  <TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      Сповіщень не знайдено.
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map(n => (
                    <TableRow hover key={n.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(n.createdAt).toLocaleString('uk-UA')}
                      </TableCell>
                      <TableCell>{n.message}</TableCell>
                      <TableCell>
                        <Chip
                          label={LEVEL_LABELS[n.level]}
                          color={LEVEL_COLORS[n.level]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={n.isRead ? 'Прочитано' : 'Непрочитано'}
                          color={n.isRead ? 'default' : 'primary'}
                          variant={n.isRead ? 'outlined' : 'filled'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          total={total}
          limit={limit}
          onPageChange={setPage}
storageKey="pagination:client-notifications"
        />
      </PageCard>
    </Box>
  );
};
